import { FoodsIcon, FoodsIconButton } from "@foodpilot/foods";
import { Box, Typography, Stack, Button, useTheme } from "@mui/material";
import { ChangeEvent, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "react-query";
import { ApplicationParameterKeys, appParamCacheKey } from "src/api/client-api/appParams/useApplicationParameters";
import { useDeleteAppParam } from "src/api/client-api/appParams/useDeleteAppParam";
import { useUpdateFile } from "src/api/client-api/appParams/useUpsertAppParam";

type Props = {
    title: string;
    subtitle?: string;
    description?: string;
    appParamKey: ApplicationParameterKeys;
    currentFile: string | undefined;
};
export const UploadImg = (props: Props) => {
    const theme = useTheme();
    const { t } = useTranslation();

    const { title, subtitle, description, currentFile, appParamKey } = props;

    const queryClient = useQueryClient();
    const updateAppParam = useUpdateFile({ forceReload: false });
    const deleteAppParam = useDeleteAppParam({ forceReload: false });

    const newSelection = useRef<HTMLInputElement>(null);
    const [newSelectedImg, setNewSelectedImg] = useState<File | null>(null);

    const handleUpload = (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            setNewSelectedImg(file);
            // Clear the input value to allow selecting the same file again
            event.target.value = "";
        }
    };

    const uploadImg = () => {
        if (newSelectedImg === null) {
            return;
        }
        const payload = {
            key: appParamKey,
            value: newSelectedImg,
        };
        updateAppParam.mutate(payload, {
            onSuccess: () => {
                queryClient.invalidateQueries([appParamCacheKey]);
                setNewSelectedImg(null);
            },
            onError: (error: any) => {},
        });
    };

    return (
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Box display="flex" flexDirection="column">
                <Typography variant="small-medium" sx={{ lineHeight: "1.6em", color: "#393A36", marginBottom: "-2px" }}>
                    {title}
                </Typography>
                <Typography variant="small" sx={{ lineHeight: "1.6em", color: "#767673" }}>
                    {subtitle}
                </Typography>
            </Box>
            <Stack gap={theme.spacing(1)}>
                <Box sx={{ display: "flex", alignItems: "center", gap: "16px" }}>
                    <Typography variant="small" sx={{ lineHeight: "1.6em", color: "#767673" }}>
                        {description}
                    </Typography>
                    {currentFile ?
                        <img
                            src={currentFile}
                            alt="preview existing file"
                            style={{ maxWidth: "150px", maxHeight: "150px" }}
                        />
                    :   <Box
                            sx={{
                                width: "176px",
                                height: "57px",
                                backgroundColor: "#EFEDEC",
                                borderRadius: "8px",
                                border: "1px solid #E7E6E4",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <Typography variant="small" sx={{ color: "#767673" }}>
                                {t("theme.noImage", "No image")}
                            </Typography>
                        </Box>
                    }
                    {(newSelectedImg || currentFile) && (
                        <FoodsIconButton
                            icon={"delete"}
                            size={"s"}
                            withBackground
                            buttonProps={{
                                onClick: () => {
                                    deleteAppParam.mutate(
                                        { key: appParamKey },
                                        {
                                            onError: (error: any) => {
                                                if (error.response?.status === 404) {
                                                    console.warn(
                                                        "Parameter not found - this is expected for new parameters",
                                                    );
                                                }
                                                queryClient.refetchQueries([appParamCacheKey]);
                                            },
                                            onSuccess: () => {
                                                queryClient.refetchQueries([appParamCacheKey]);
                                                setNewSelectedImg(null);
                                            },
                                        },
                                    );
                                },
                            }}
                            tooltip={{
                                text: t("theme.delete_img", "Delete an image"),
                            }}
                        />
                    )}
                    <Button
                        variant={"primary"}
                        onClick={() => {
                            newSelection.current?.click();
                        }}
                        startIcon={<FoodsIcon icon="importUpload" size={1.25} />}
                    >
                        {t("theme.select_img", "Select an image")}
                    </Button>
                    <input
                        ref={newSelection}
                        type="file"
                        accept="image/*"
                        onChange={handleUpload}
                        style={{
                            display: "none",
                        }}
                    />
                </Box>
                {newSelectedImg && (
                    <Stack flexDirection="row" gap={theme.spacing(2)} justifyContent={"end"}>
                        <img
                            src={URL.createObjectURL(newSelectedImg)}
                            alt="Preview new file"
                            style={{ maxWidth: "150px", maxHeight: "40px" }}
                        />
                        <Button
                            variant="contained"
                            onClick={() => {
                                uploadImg();
                            }}
                            startIcon={<FoodsIcon icon="importUpload" size={1.25} />}
                        >
                            {t("theme.upload_img", "Upload")}
                        </Button>
                    </Stack>
                )}
            </Stack>
        </Box>
    );
};
